import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import Button from 'components/UI/Button';
import InlineError from 'components/UI/InlineError';
import { getCoupons } from 'modules/loyaltyPoints/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { ROUTE_PATH } from 'routes';
import useLiterals from 'utils/hooks/useLiterals';
import { ReactComponent as CupIcon } from 'assets/icons/cup.svg';
import Box from 'components/UI/Box/Box';
import { formatDate } from 'utils/formatDate';
import { formatCurrency } from 'utils/formatCurrency';
import Barcode from 'react-barcode';
import { ReactComponent as MedalRibbonsStarLogo } from 'assets/icons/medal-ribbons-star.svg';
import html2canvas from 'html2canvas';
import { ReactComponent as IconDownload } from 'assets/icons/download-white.svg';
import { jsPDF as JsPDF } from 'jspdf';
import { Badge } from 'react-bootstrap';
import COUPON_STATUS from 'constants/loyaltyPoints';

const LoyaltyPointsCoupon = () => {
  const { id } = useParams();
  const { coupons: { data: coupons, error: couponsError } } = useSelector((state) => state.loyaltyPoints);
  const literals = useLiterals('loyaltyPoints');
  const dispatch = useDispatch();
  const { state } = useLocation();
  const [generating, setGenerating] = useState(false);
  const isNew = !!state?.isNew;
  const zonaRef = useRef();

  useEffect(() => {
    dispatch(getCoupons());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const coupon = useMemo(() => (coupons || [])?.find((c) => c.id.toString() === id.toString()), [coupons, id]);

  const handleCapture = async () => {
    setGenerating(true);
    const canvas = await html2canvas(zonaRef.current);
    const base64Image = canvas.toDataURL('image/png');
    const PDF = new JsPDF('p', 'mm', 'a4');
    const pageWidth = 210; // Ancho de página A4 en mm
    const pageHeight = 297; // Alto de página A4 en mm
    const margin = 20; // Margen en mm
    const maxImgWidth = pageWidth - (margin * 2); // Ancho máximo disponible
    const imgWidth = Math.min(maxImgWidth, 120); // Máximo 120mm de ancho
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const xPosition = (pageWidth - imgWidth) / 2;
    const yPosition = (pageHeight - imgHeight) / 2;
    PDF.addImage(base64Image, 'PNG', xPosition, yPosition, imgWidth, imgHeight);
    const pdfBase64 = PDF.output('datauristring');
    window.share(`${literals.coupon} ${coupon.id}`, pdfBase64, 'application/pdf', true);
    setGenerating(false);
  };

  const renderLabelStatus = () => {
    if (!coupon) {
      return null;
    }
    if (coupon.status === COUPON_STATUS.USED) {
      return <Badge bg='success' className='w-full mb-4 text-base'>{literals.couponUsed}</Badge>;
    }
    if (coupon.status === COUPON_STATUS.EXPIRED) {
      return <Badge bg='danger' className='w-full mb-4 text-base'>{literals.couponExpired}</Badge>;
    }
    return null;
  };

  return (
    <Layout
      className='loyalty-points-coupon'
      bottomMenu
      onBack
    >
      <AppShell>
        {(couponsError || (!coupon && Array.isArray(coupons))) ? (
          <InlineError error={literals.errorLoadCoupon} className='mt-12' />
        ) : (
          <div>
            {isNew && (
              <div className='flex flex-col items-center mb-4 gap-2'>
                <CupIcon width={48} />
                <p className='text-center font-semibold text-lg'>{literals.couponCreated}</p>
              </div>
            )}
            {renderLabelStatus()}
            {coupon ? (
              <>
                <Box padding='sm' className='mb-12' ref={zonaRef}>
                  <div className='flex items-center gap-2 mb-4'>
                    <MedalRibbonsStarLogo width={48} />
                    <p className='mb-0 font-bold text-lg'>{coupon.description || '-'}</p>
                  </div>
                  <Barcode className='w-full h-auto' value={coupon.code} />
                  <p className='text-sm text-neutral-500 mt-2 mb-2'>{`#${coupon.promotionId}`}</p>
                  <div className='flex items-center gap-2 mb-2'>
                    <p className='mb-0'>{literals.maxAmount}</p>
                    <p className='mb-0 font-semibold'>{formatCurrency(coupon.maxAmount)}</p>
                  </div>
                  {(coupon.couponType || coupon.legacyCouponType) ? (
                    <div className='flex items-center gap-2 mb-2'>
                      <p className='mb-0'>{literals.couponType}</p>
                      <p className='mb-0 font-semibold'>
                        {coupon.couponType || literals.legacyCouponType}
                      </p>
                    </div>
                  ) : null}
                  <div className='flex items-center gap-2 mb-2'>
                    <p className='mb-0'>{literals.discount}</p>
                    <p className='mb-0 font-semibold'>{`${coupon.discount}%`}</p>
                  </div>
                  {coupon.sections.length ? (
                    <div className='flex items-center gap-2 mb-2'>
                      <p className='mb-0'>{literals.applyIn}</p>
                      <p className='mb-0 font-semibold'>{coupon.sections.map((section) => literals.sections[section] || section).join(', ')}</p>
                    </div>
                  ) : null}
                  <div className='flex items-center gap-2 mb-2'>
                    <p className='mb-0'>{literals.life}</p>
                    <p className='mb-0 font-semibold'>{formatDate(coupon.endDate, 'DD/MM/YYYY')}</p>
                  </div>
                  <div className='flex items-center gap-2 mb-0'>
                    <p className='mb-0'>{literals.clientNumber}</p>
                    <p className='mb-0 font-semibold'>{coupon.clientId}</p>
                  </div>
                  {coupon.autosMessage ? (
                    <p className='alert alert-warning text-sm mt-4 mb-0' role='alert'>
                      {coupon.autosMessage}
                    </p>
                  ) : null}
                </Box>
                <Button onClick={handleCapture} className='mb-4 flex items-center gap-2 justify-center' loading={generating}>
                  <IconDownload width={16} />
                  {literals.common.download}
                </Button>
              </>
            ) : null}
          </div>
        )}
        {isNew && (
          <Button type='secondary' to={ROUTE_PATH.LOYALTY_POINTS_COUPONS}>{literals.couponHistory}</Button>
        )}
      </AppShell>
    </Layout>
  );
};

export default LoyaltyPointsCoupon;
